import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { User, Package, CalendarDays, LogOut, Loader2, Pencil, Check, ExternalLink } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const statusColors = {
  pending: "text-yellow-400 bg-yellow-400/10 border-yellow-400/30",
  confirmed: "text-blue-400 bg-blue-400/10 border-blue-400/30",
  completed: "text-green-400 bg-green-400/10 border-green-400/30",
  cancelled: "text-red-400 bg-red-400/10 border-red-400/30",
  paid: "text-green-400 bg-green-400/10 border-green-400/30",
  failed: "text-red-400 bg-red-400/10 border-red-400/30",
};

function StatusBadge({ status }) {
  const c = statusColors[status] || "text-gray-400 bg-gray-400/10 border-gray-400/30";
  return <span className={`inline-block px-3 py-1 text-xs font-sans tracking-wider uppercase border ${c}`}>{status}</span>;
}

function ProfileSection({ user, updateProfile }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: user.name, phone: user.phone || "" });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfile(form);
      toast.success("Profile updated");
      setEditing(false);
    } catch { toast.error("Update failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-[#161616] border border-[#b8966a]/10 p-8" data-testid="profile-section">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-xl text-[#f8f5f0]">Your Profile</h2>
        {!editing && (
          <button onClick={() => setEditing(true)} className="flex items-center gap-1 font-sans text-xs tracking-wider uppercase text-[#b8966a] hover:text-[#cfae82] transition-colors" data-testid="edit-profile-btn">
            <Pencil size={14} /> Edit
          </button>
        )}
      </div>
      {editing ? (
        <div className="space-y-4">
          <div>
            <label className="overline block mb-2">Name</label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              className="w-full bg-[#0c0c0c] border border-[#b8966a]/20 px-4 py-3 text-[#f8f5f0] font-body focus:border-[#b8966a] transition-colors" data-testid="profile-name-input" />
          </div>
          <div>
            <label className="overline block mb-2">Phone</label>
            <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
              className="w-full bg-[#0c0c0c] border border-[#b8966a]/20 px-4 py-3 text-[#f8f5f0] font-body focus:border-[#b8966a] transition-colors" data-testid="profile-phone-input" />
          </div>
          <div className="flex gap-3">
            <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="save-profile-btn">
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />} Save
            </button>
            <button onClick={() => setEditing(false)} className="btn-secondary">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-6">
          <div><p className="overline text-[10px] mb-1">Name</p><p className="font-body text-[#f8f5f0]">{user.name}</p></div>
          <div><p className="overline text-[10px] mb-1">Email</p><p className="font-body text-[#f8f5f0]">{user.email}</p></div>
          <div><p className="overline text-[10px] mb-1">Phone</p><p className="font-body text-[#f8f5f0]">{user.phone || "Not set"}</p></div>
        </div>
      )}
    </div>
  );
}

function OrdersSection() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const res = await axios.get(`${API}/account/orders`, { withCredentials: true }); setOrders(res.data); }
      catch (e) { console.error("Failed to load orders:", e); } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#b8966a]" size={24} /></div>;

  return (
    <div data-testid="orders-section">
      <h2 className="font-heading text-xl text-[#f8f5f0] mb-6">Order History</h2>
      {orders.length === 0 ? (
        <div className="bg-[#161616] border border-[#b8966a]/10 p-8 text-center">
          <Package className="text-[#b8966a]/40 mx-auto mb-3" size={32} />
          <p className="font-body text-[#a19d98] mb-4">No orders yet</p>
          <Link to="/collection" className="btn-secondary inline-block">Browse Collection</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map(order => (
            <div key={order.id} className="bg-[#161616] border border-[#b8966a]/10 p-6">
              <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
                <div>
                  <p className="font-heading text-lg text-[#f8f5f0]">{order.metadata?.product_name || "Order"}</p>
                  <p className="font-body text-xs text-[#a19d98]">{order.created_at ? new Date(order.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : ""}</p>
                </div>
                <StatusBadge status={order.payment_status} />
              </div>
              <div className="flex items-center gap-6">
                <p className="font-sans text-lg text-[#b8966a]">${order.amount?.toFixed(2)}</p>
                <p className="font-body text-sm text-[#a19d98] capitalize">{order.payment_method}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BookingsSection() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const res = await axios.get(`${API}/account/bookings`, { withCredentials: true }); setBookings(res.data); }
      catch (e) { console.error("Failed to load bookings:", e); } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#b8966a]" size={24} /></div>;

  return (
    <div data-testid="bookings-section">
      <h2 className="font-heading text-xl text-[#f8f5f0] mb-6">Booking History</h2>
      {bookings.length === 0 ? (
        <div className="bg-[#161616] border border-[#b8966a]/10 p-8 text-center">
          <CalendarDays className="text-[#b8966a]/40 mx-auto mb-3" size={32} />
          <p className="font-body text-[#a19d98] mb-4">No bookings yet</p>
          <Link to="/floral-bar" className="btn-secondary inline-block">Book The Floral Bar</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {bookings.map(b => (
            <div key={b.id} className="bg-[#161616] border border-[#b8966a]/10 p-6">
              <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
                <div>
                  <p className="font-heading text-lg text-[#f8f5f0]">{b.booking_type?.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}</p>
                  <p className="font-body text-xs text-[#a19d98]">{b.preferred_date} at {b.preferred_time}</p>
                </div>
                <StatusBadge status={b.status} />
              </div>
              {b.notes && <p className="font-body text-sm text-[#a19d98] italic">"{b.notes}"</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const tabs = [
  { id: "profile", label: "Profile", icon: <User size={16} /> },
  { id: "orders", label: "Orders", icon: <Package size={16} /> },
  { id: "bookings", label: "Bookings", icon: <CalendarDays size={16} /> },
];

export default function Account() {
  const { user, loading, logout, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("profile");

  useEffect(() => {
    if (!loading && !user) navigate("/auth");
  }, [user, loading, navigate]);

  if (loading || !user) {
    return <div className="pt-[72px] min-h-screen bg-[#0c0c0c] flex items-center justify-center"><Loader2 className="animate-spin text-[#b8966a]" size={32} /></div>;
  }

  return (
    <div className="page-transition pt-[72px] min-h-screen bg-[#0c0c0c]" data-testid="account-page">
      <div className="container-custom py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="overline mb-2">My Account</p>
            <h1 className="font-display text-3xl text-[#f8f5f0]">Welcome, {user.name}</h1>
          </div>
          <button onClick={() => { logout(); navigate("/"); }} className="flex items-center gap-2 font-sans text-xs tracking-wider uppercase text-[#a19d98] hover:text-[#b8966a] transition-colors" data-testid="account-logout">
            <LogOut size={16} /> Sign Out
          </button>
        </div>

        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 font-sans text-xs tracking-wider uppercase border transition-all whitespace-nowrap ${
                activeTab === tab.id ? "bg-[#b8966a] border-[#b8966a] text-[#0c0c0c]" : "bg-transparent border-[#b8966a]/20 text-[#a19d98] hover:border-[#b8966a]/50"
              }`} data-testid={`account-tab-${tab.id}`}>{tab.icon}{tab.label}</button>
          ))}
        </div>

        {activeTab === "profile" && <ProfileSection user={user} updateProfile={updateProfile} />}
        {activeTab === "orders" && <OrdersSection />}
        {activeTab === "bookings" && <BookingsSection />}
      </div>
    </div>
  );
}
